// @file image processing utils

import { captureException } from '@@/bits/error_tracker'
import { memoize } from 'es-toolkit'

/**
 * Extracts the first frame of a GIF image and returns it as a PNG data URL.
 * This is useful for showing a static preview of GIF images when they are paused
 * or when animated content should not be shown (e.g., reduced motion mode).
 *
 * @param url - The URL of the GIF image to extract the first frame from
 * @returns A Promise that resolves to a data URL containing the first frame as a PNG,
 *          or null if the extraction fails
 *
 * @example
 * ```typescript
 * const firstFrame = await extractGifFirstFrame('https://example.com/animation.gif')
 * if (firstFrame) {
 *   // Use the data URL in an img src attribute
 *   imageElement.src = firstFrame
 * }
 * ```
 */

async function _extractGifFirstFrameCore(url: string): Promise<string> {
  const response = await fetch(url)
  const blob = await response.blob()
  const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' })

  // Create a canvas to draw the first frame
  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  const ctx = canvas.getContext('2d')
  if (ctx == null) throw new Error('Could not get canvas context')

  // Draw the first frame
  ctx.drawImage(bitmap, 0, 0)

  // Convert to data URL
  const dataUrl = canvas.toDataURL('image/png')

  // Cleanup
  bitmap.close()

  return dataUrl
}

const memoizedExtractGifFirstFrame = memoize(_extractGifFirstFrameCore)

export async function extractGifFirstFrame(url: string): Promise<string | null> {
  try {
    return await memoizedExtractGifFirstFrame(url)
  } catch (error) {
    captureException(error)
    return null
  }
}
